import { get, set } from 'idb-keyval';
import useIsNative from '@/hooks/native/useIsNative';
import useNativeMessages, {
	splitKeyValue,
} from '@/hooks/native/useNativeMessages';
import { useCallback } from 'react';
import { spreadPromiseShortDelay } from '@/lib/time';

const useStorage = () => {
	const isNative = useIsNative();

	const { sendNativeMessage, awaitSingleNativeMessage } = useNativeMessages();

	// Save a file as base64 encoded string
	const saveFileBase64 = useCallback(
		async (filename: string, base64: string) => {
			if (isNative) {
				sendNativeMessage(
					'storage.saveFileBase64',
					`${filename}=${base64}`
				);
				await awaitSingleNativeMessage('storage.saveFileBase64');
			}
			set(filename, base64);
		},
		[awaitSingleNativeMessage, isNative, sendNativeMessage]
	);

	// Read a file as base64 encoded string
	const readFileBase64 = useCallback(
		async (filename: string) => {
			await spreadPromiseShortDelay();
			try {
				if (isNative) {
					sendNativeMessage('storage.readFileBase64', filename);
					const { value } = splitKeyValue(
						await awaitSingleNativeMessage(
							'storage.readFileBase64',
							filename
						)
					);
					return value;
				} else {
					return await get(filename);
				}
			} catch (e) {
				console.error(e);
				return null;
			}
		},
		[awaitSingleNativeMessage, isNative, sendNativeMessage]
	);

	// Get a photo thumbnail with given dimensions
	const readImageThumbnailBase64 = useCallback(
		async (filename: string, x: number, y: number) => {
			await spreadPromiseShortDelay();
			try {
				if (isNative) {
					// Send message with filename and dimensions
					const nativeMessageStart = `${filename}=${x}=${y}`;
					sendNativeMessage(
						'storage.readImageThumbnailBase64',
						nativeMessageStart
					);
					const value = (
						await awaitSingleNativeMessage(
							'storage.readImageThumbnailBase64',
							nativeMessageStart
						)
					).replace(`${nativeMessageStart}=`, '');
					return value;
				} else {
					return await get(filename);
				}
			} catch (e) {
				console.error(e);
				return null;
			}
		},
		[awaitSingleNativeMessage, isNative, sendNativeMessage]
	);

	const deleteFile = useCallback(
		async (filename: string) => {
			if (isNative) {
				sendNativeMessage('storage.deleteFile', filename);
				await awaitSingleNativeMessage('storage.deleteFile');
			}
			await set(filename, undefined);
		},
		[awaitSingleNativeMessage, isNative, sendNativeMessage]
	);

	return {
		saveFileBase64,
		readFileBase64,
		readImageThumbnailBase64,
		deleteFile,
	};
};

export default useStorage;
